import { jsPDF } from "jspdf";
import { saveAs } from "file-saver";
import { Document, Packer, Paragraph, TextRun, HeadingLevel } from "docx";

function toText(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

function escapeCsv(value) {
  const text = toText(value).replace(/"/g, '""');
  return `"${text}"`;
}

function formatAddress(client) {
  return [client.adres, client.postcode, client.plaats].filter(Boolean).join(", ");
}

function rowFromClient(client) {
  const contacts = Array.isArray(client.contactPersons) ? client.contactPersons : [];
  const defaultContact = contacts.find((item) => item?.isDefault) || contacts[0] || null;

  return {
    id: client._id || "",
    naam: client.naam || "",
    status: client.actief === false ? "inactief" : "actief",
    email: client.email || defaultContact?.email || "",
    telefoon: client.telefoon || defaultContact?.phone || "",
    adres: formatAddress(client),
    kvkNummer: client.kvkNummer || "",
    btwNummer: client.btwNummer || "",
    facturatieStatus: client.facturatieStatus || "",
    contactPersonen: contacts.length,
    moneybirdId: client.integrations?.moneybird?.id || "",
    updatedAt: client.updatedAt || "",
  };
}

function downloadBlob(filename, blob, type) {
  const effectiveBlob = blob instanceof Blob ? blob : new Blob([blob], { type });
  saveAs(effectiveBlob, filename);
}

function buildCsv(rows) {
  const headers = [
    "id",
    "naam",
    "status",
    "email",
    "telefoon",
    "adres",
    "kvkNummer",
    "btwNummer",
    "facturatieStatus",
    "contactPersonen",
    "moneybirdId",
    "updatedAt",
  ];

  const lines = [headers.map(escapeCsv).join(",")];
  rows.forEach((row) => {
    lines.push(headers.map((key) => escapeCsv(row[key])).join(","));
  });

  return `${lines.join("\n")}\n`;
}

async function exportPdf(rows, filename) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const marginLeft = 40;
  const pageHeight = doc.internal.pageSize.height;
  let y = 40;

  doc.setFontSize(16);
  doc.text("Klant Export", marginLeft, y);
  y += 22;

  doc.setFontSize(10);
  doc.text(`Aantal klanten: ${rows.length}`, marginLeft, y);
  y += 16;
  doc.text(`Gegenereerd: ${new Date().toISOString()}`, marginLeft, y);
  y += 22;

  rows.forEach((row, index) => {
    const lines = [
      `${index + 1}. ${row.naam} (${row.status})`,
      `   Email: ${row.email || "-"} | Telefoon: ${row.telefoon || "-"}`,
      `   Adres: ${row.adres || "-"}`,
      `   KVK: ${row.kvkNummer || "-"} | BTW: ${row.btwNummer || "-"}`,
      `   Facturatie: ${row.facturatieStatus || "-"} | Contactpersonen: ${row.contactPersonen}`,
      "",
    ];

    lines.forEach((line) => {
      if (y > pageHeight - 40) {
        doc.addPage();
        y = 40;
      }
      doc.text(line, marginLeft, y);
      y += 14;
    });
  });

  doc.save(filename);
}

async function exportDocx(rows, filename) {
  const children = [
    new Paragraph({ text: "Klant Export", heading: HeadingLevel.HEADING_1 }),
    new Paragraph({ text: `Aantal klanten: ${rows.length}` }),
    new Paragraph({ text: `Gegenereerd: ${new Date().toISOString()}` }),
    new Paragraph({ text: "" }),
  ];

  rows.forEach((row, index) => {
    children.push(
      new Paragraph({
        children: [
          new TextRun({ text: `${index + 1}. ${row.naam}`, bold: true }),
          new TextRun({ text: ` (${row.status})` }),
        ],
      })
    );
    children.push(new Paragraph(`Email: ${row.email || "-"}`));
    children.push(new Paragraph(`Telefoon: ${row.telefoon || "-"}`));
    children.push(new Paragraph(`Adres: ${row.adres || "-"}`));
    children.push(new Paragraph(`KVK: ${row.kvkNummer || "-"} | BTW: ${row.btwNummer || "-"}`));
    children.push(new Paragraph(`Facturatie: ${row.facturatieStatus || "-"} | Contactpersonen: ${row.contactPersonen}`));
    children.push(new Paragraph(""));
  });

  const doc = new Document({
    sections: [
      {
        properties: {},
        children,
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  downloadBlob(filename, blob, "application/vnd.openxmlformats-officedocument.wordprocessingml.document");
}

export async function exportClients({ clients, format = "json", filenameBase = "klanten-export" }) {
  const rows = (Array.isArray(clients) ? clients : []).map(rowFromClient);

  if (!rows.length) {
    throw new Error("Geen klanten om te exporteren.");
  }

  if (format === "json") {
    downloadBlob(`${filenameBase}.json`, JSON.stringify(rows, null, 2), "application/json");
    return;
  }

  if (format === "csv") {
    downloadBlob(`${filenameBase}.csv`, buildCsv(rows), "text/csv;charset=utf-8");
    return;
  }

  if (format === "pdf") {
    await exportPdf(rows, `${filenameBase}.pdf`);
    return;
  }

  if (format === "docx") {
    await exportDocx(rows, `${filenameBase}.docx`);
    return;
  }

  throw new Error("Onbekend exportformaat.");
}

function hubSummaryRow(clientHub) {
  return {
    id: clientHub?.client?.id || "",
    naam: clientHub?.client?.bedrijfsnaam || "",
    status: clientHub?.client?.status || "",
    email: clientHub?.client?.email || "",
    telefoon: clientHub?.client?.telefoon || "",
    adres: clientHub?.client?.bezoekadres || "",
    kvkNummer: clientHub?.client?.kvkNummer || "",
    btwNummer: clientHub?.client?.btwNummer || "",
    contactPersonen: Array.isArray(clientHub?.contacts) ? clientHub.contacts.length : 0,
    werkbonnen: Array.isArray(clientHub?.workorders) ? clientHub.workorders.length : 0,
    documenten: Array.isArray(clientHub?.documents) ? clientHub.documents.length : 0,
    taken: Array.isArray(clientHub?.tasks) ? clientHub.tasks.length : 0,
  };
}

export async function exportClientHub({ clientHub, format = "json", filenameBase = "klant-dossier" }) {
  if (!clientHub?.client) {
    throw new Error("Geen klantdossier om te exporteren.");
  }

  const summary = hubSummaryRow(clientHub);

  if (format === "json") {
    downloadBlob(`${filenameBase}.json`, JSON.stringify(clientHub, null, 2), "application/json");
    return;
  }

  if (format === "csv") {
    const headers = [
      "id",
      "naam",
      "status",
      "email",
      "telefoon",
      "adres",
      "kvkNummer",
      "btwNummer",
      "contactPersonen",
      "werkbonnen",
      "documenten",
      "taken",
    ];
    const csv = `${headers.map(escapeCsv).join(",")}\n${headers.map((key) => escapeCsv(summary[key])).join(",")}\n`;
    downloadBlob(`${filenameBase}.csv`, csv, "text/csv;charset=utf-8");
    return;
  }

  if (format === "pdf") {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    let y = 40;
    const left = 40;
    const pageHeight = doc.internal.pageSize.height;
    const lines = [
      `Klantdossier: ${summary.naam}`,
      `Status: ${summary.status}`,
      `Email: ${summary.email || "-"}`,
      `Telefoon: ${summary.telefoon || "-"}`,
      `Adres: ${summary.adres || "-"}`,
      `KVK: ${summary.kvkNummer || "-"} | BTW: ${summary.btwNummer || "-"}`,
      `Contactpersonen: ${summary.contactPersonen}`,
      `Werkbonnen: ${summary.werkbonnen}`,
      `Documenten: ${summary.documenten}`,
      `Taken: ${summary.taken}`,
      "",
      "Werkbonnen:",
      ...(clientHub.workorders || []).map((item) => `- ${item.werkbonnummer} | ${item.titel} | ${item.status}`),
      "",
      "Documenten:",
      ...(clientHub.documents || []).map((item) => `- ${item.bestandsnaam} (${item.documenttype})`),
    ];
    doc.setFontSize(11);
    lines.forEach((line) => {
      if (y > pageHeight - 40) {
        doc.addPage();
        y = 40;
      }
      doc.text(String(line), left, y);
      y += 14;
    });
    doc.save(`${filenameBase}.pdf`);
    return;
  }

  if (format === "docx") {
    const children = [
      new Paragraph({ text: `Klantdossier: ${summary.naam}`, heading: HeadingLevel.HEADING_1 }),
      new Paragraph(`Status: ${summary.status}`),
      new Paragraph(`Email: ${summary.email || "-"}`),
      new Paragraph(`Telefoon: ${summary.telefoon || "-"}`),
      new Paragraph(`Adres: ${summary.adres || "-"}`),
      new Paragraph(`KVK: ${summary.kvkNummer || "-"} | BTW: ${summary.btwNummer || "-"}`),
      new Paragraph(`Contactpersonen: ${summary.contactPersonen}`),
      new Paragraph(`Werkbonnen: ${summary.werkbonnen}`),
      new Paragraph(`Documenten: ${summary.documenten}`),
      new Paragraph(`Taken: ${summary.taken}`),
      new Paragraph(""),
      new Paragraph({ text: "Werkbonnen", heading: HeadingLevel.HEADING_2 }),
      ...(clientHub.workorders || []).map((item) => new Paragraph(`- ${item.werkbonnummer} | ${item.titel} | ${item.status}`)),
      new Paragraph(""),
      new Paragraph({ text: "Documenten", heading: HeadingLevel.HEADING_2 }),
      ...(clientHub.documents || []).map((item) => new Paragraph(`- ${item.bestandsnaam} (${item.documenttype})`)),
    ];

    const doc = new Document({
      sections: [{ properties: {}, children }],
    });
    const blob = await Packer.toBlob(doc);
    downloadBlob(
      `${filenameBase}.docx`,
      blob,
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    );
    return;
  }

  throw new Error("Onbekend exportformaat.");
}
